// WebSocket RPC protocol between UI (browser) and server (Node).
// Replaces the old Web Worker postMessage RPC transport.
//
// Calling convention mirrors the original `[type, name, param]` tuple used by
// promiseWorker.postMessage / toWorker, so the dispatcher on each side can
// reuse the existing handler shape with zero changes.

// Worker API namespaces — must match the union exported as WorkerAPICategory
// from server/worker/index.ts. Inlined here (rather than imported) so this
// module doesn't drag the whole worker type graph into consumers that only
// need the protocol shape (e.g. the server build).
export type WsCallType =
	| "actions"
	| "main"
	| "playMenu";

export type WsCall = {
	id: number;
	kind: "call";
	// Originator of the call. In phase 1 every UI uses the constant
	// `DEFAULT_USER_ID`; once auth lands (multi-user phase) this becomes the
	// authenticated user id. Carried on every RPC for future authorization +
	// per-user push routing.
	userId: string;
	// Target league. `null` means a system-level RPC that isn't bound to any
	// league (createLeague, listLeagues, account, etc.). Once worker threads
	// land (phase 2/3) the main dispatcher uses this to pick a thread.
	lid: number | null;
	type: WsCallType;
	name: string;
	param: unknown;
};

export type WsResult = {
	id: number;
	kind: "result";
	result: unknown;
};

export type WsError = {
	id: number;
	kind: "error";
	message: string;
	stack?: string;
};

export type WsPushResult = {
	id: number;
	kind: "pushResult";
	lid: number | null;
	result: unknown;
};

export type WsPushError = {
	id: number;
	kind: "pushError";
	lid: number | null;
	message: string;
	stack?: string;
};

// --- Auth handshake (phase 11.2) ---
//
// First frame on every new WS connection. The server gates all subsequent
// WsCall frames behind a successful handshake and uses the session's
// authoritative userId rather than whatever the client puts in WsCall.userId.
//
// Two paths (see MULTI_USER_PHASE_11_AUTH_WORKFLOW.md §11.3):
//   - With `token`: resume an existing session
//   - With `deviceId` (no token): signup/signin via device credential
// Both may be absent in 11.2's dev handler, which auto-creates a session.

export type WsAuth = {
	kind: "auth";
	deviceId?: string;
	token?: string;
};

export type WsAuthOk = {
	kind: "authOk";
	userId: string;
	handle: string;
	token: string;
};

export type WsAuthFail = {
	kind: "authFail";
	message: string;
};

// Server-initiated push (corresponds to worker → main toUI).
// `name` is the UI API method name; `args` is the spread arg list.
// `lid` identifies the source league (`null` = system / not league-scoped).
// Once worker threads land each league worker tags its pushes with its own
// lid so the main process / UI can route them; in phase 1 there is only one
// main process so `lid` is always `null`.
export type WsPush = {
	id?: number;
	kind: "push";
	lid: number | null;
	name: string;
	args: unknown[];
};

export type WsClientMessage = WsCall | WsAuth | WsPushResult | WsPushError;
export type WsServerMessage = WsResult | WsError | WsPush | WsAuthOk | WsAuthFail;

export const WS_PATH = "/ws";
export const DEFAULT_SERVER_PORT = 4000;

// Placeholder until the user auth system lands (multi-user phase 11+). For
// now every UI sends this on every call. The server-side authorization
// scaffold (phase 6) will read it; in phase 1 it's recorded but not used.
export const DEFAULT_USER_ID = "default-user-1";

/**
 * Server-side runtime check for a parsed WsCall. Returns `null` if the shape
 * is valid, otherwise a short reason string suitable for an error reply.
 *
 * Necessary because the wire is JSON — TypeScript types only constrain what
 * the UI bundle emits, not what arbitrary clients send. Without this an
 * old/buggy client could drop `userId`/`lid` and the dispatcher would route
 * `lid: undefined` to a phantom worker thread.
 */
/**
 * Coarse shape check for an inbound auth frame. The handler will produce
 * its own semantic errors (invalid token, etc.) — this just guards the
 * dispatcher from undefined-shaped messages.
 */
export const validateWsAuth = (msg: WsAuth): string | null => {
	if (
		msg.deviceId !== undefined &&
		(typeof msg.deviceId !== "string" || msg.deviceId.length === 0)
	) {
		return "deviceId must be a non-empty string when present";
	}
	if (
		msg.token !== undefined &&
		(typeof msg.token !== "string" || msg.token.length === 0)
	) {
		return "token must be a non-empty string when present";
	}
	return null;
};

export const validateWsCall = (msg: WsCall): string | null => {
	if (typeof msg.id !== "number" || !Number.isFinite(msg.id)) {
		return "missing or non-numeric id";
	}
	if (typeof msg.userId !== "string" || msg.userId.length === 0) {
		return "missing or empty userId";
	}
	if (msg.lid !== null && (typeof msg.lid !== "number" || !Number.isInteger(msg.lid))) {
		return "lid must be null or an integer";
	}
	if (typeof msg.type !== "string" || msg.type.length === 0) {
		return "missing type";
	}
	if (typeof msg.name !== "string" || msg.name.length === 0) {
		return "missing name";
	}
	return null;
};

export const validateWsPushResponse = (
	msg: WsPushResult | WsPushError,
): string | null => {
	if (typeof msg.id !== "number" || !Number.isFinite(msg.id)) {
		return "missing or non-numeric push response id";
	}
	if (msg.lid !== null && (typeof msg.lid !== "number" || !Number.isInteger(msg.lid))) {
		return "push response lid must be null or an integer";
	}
	if (msg.kind === "pushError" && typeof msg.message !== "string") {
		return "push error message must be a string";
	}
	return null;
};
